import React from 'react';
import { Paper, Typography, Box, Chip, Stack } from '@mui/material';
import {
  DirectionsCar as CarIcon,
  DirectionsBus as BusIcon,
  DirectionsWalk as WalkIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in React-Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
});

interface ContactMapProps {
  studentAddress: string;
  schoolAddress?: string;
}

// Custom icons for markers
const studentIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const schoolIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

export const ContactMap: React.FC<ContactMapProps> = ({ 
  studentAddress,
  schoolAddress = '456 Academy Ave'
}) => {
  // Mock coordinates (in a real app, these would come from geocoding the addresses)
  const studentLocation: [number, number] = [40.7128, -74.0060]; // Example coordinates
  const schoolLocation: [number, number] = [40.7150, -74.0047]; // Example coordinates

  // Calculate mock distance (in a real app, this would use actual geocoding)
  const distance = '2.3 miles';

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Location & Travel Info
      </Typography>

      <Box sx={{ height: 400, mb: 3 }}>
        <MapContainer
          center={studentLocation}
          zoom={14}
          style={{ height: '100%', width: '100%', borderRadius: 8 }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          
          <Marker position={studentLocation} icon={studentIcon}>
            <Popup>
              <Typography variant="subtitle2">Student's Home</Typography>
              <Typography variant="body2">{studentAddress}</Typography>
            </Popup>
          </Marker>
          
          <Marker position={schoolLocation} icon={schoolIcon}>
            <Popup>
              <Typography variant="subtitle2">School</Typography>
              <Typography variant="body2">{schoolAddress}</Typography>
            </Popup>
          </Marker>

          <Polyline 
            positions={[studentLocation, schoolLocation]}
            color="#2196f3"
            weight={3}
            opacity={0.8}
            dashArray="10, 10"
          />
        </MapContainer>
      </Box>

      <Stack spacing={2}>
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Distance
          </Typography>
          <Chip 
            icon={<LocationIcon />}
            label={distance}
            variant="outlined"
          />
        </Box>

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Estimated Travel Time
          </Typography>
          <Stack direction="row" spacing={1}>
            <Chip
              icon={<CarIcon />}
              label="15 min"
              variant="outlined"
              color="primary"
            />
            <Chip
              icon={<BusIcon />}
              label="30 min"
              variant="outlined"
              color="secondary"
            />
            <Chip
              icon={<WalkIcon />}
              label="45 min"
              variant="outlined"
              color="default"
            />
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
};